/**
 * Keeps vo_dk_active_codes in sync with Shopify discount cookies + cart/update.js.
 * Storefront prices are patched client-side via vo-dk-storefront-prices.js (Liquid cannot read cookies).
 */
(function () {
  const STORAGE_KEY = 'vo_dk_session_discount_codes';
  const SHOPIFY_CODE_COOKIES = ['discount_code', 'discount'];

  const normalizeCode = (value) => String(value || '').trim().toLowerCase();

  const resolveDiscountCodeFromUrl = () => {
    const pathMatch = window.location.pathname.match(/\/discount\/([^/?#]+)/i);
    if (pathMatch?.[1]) {
      return decodeURIComponent(pathMatch[1]).trim();
    }

    const queryCode = new URLSearchParams(window.location.search).get('discount');
    return queryCode ? queryCode.trim() : '';
  };

  const getCookie = (name) => {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  };

  const readStoredCodes = () => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      return raw.split(',').map(normalizeCode).filter(Boolean);
    } catch {
      return [];
    }
  };

  const writeStoredCodes = (codes) => {
    try {
      if (!codes.length) {
        sessionStorage.removeItem(STORAGE_KEY);
        return;
      }
      sessionStorage.setItem(STORAGE_KEY, codes.join(','));
    } catch {
      /* ignore */
    }
  };

  const mergeCodes = (...lists) => {
    const codes = new Set();
    lists.flat().forEach((value) => {
      const normalized = normalizeCode(value);
      if (normalized) codes.add(normalized);
    });
    return Array.from(codes);
  };

  const buildCodesCsv = (cart, extraCodes = []) => {
    const codes = new Set(
      mergeCodes(
        extraCodes,
        SHOPIFY_CODE_COOKIES.map(getCookie).filter(Boolean),
        readStoredCodes(),
        window.VoDkStorefrontPrices?.getAppliedCodesCsv?.()
          ? window.VoDkStorefrontPrices
              .getAppliedCodesCsv()
              .split(',')
              .map(normalizeCode)
              .filter(Boolean)
          : []
      )
    );

    const add = (value) => {
      const normalized = normalizeCode(value);
      if (normalized) codes.add(normalized);
    };

    (cart?.discount_codes || []).forEach((entry) => add(entry.code));
    (cart?.discount_applications || []).forEach((app) => {
      if (app?.type === 'discount_code') {
        add(app.code);
        add(app.title);
      }
    });
    (cart?.cart_level_discount_applications || []).forEach((app) => {
      if (app?.type === 'discount_code' || app?.type === 'code') {
        add(app.title);
      }
    });

    const unique = Array.from(codes);
    return unique.length ? `,${unique.join(',')},` : '';
  };

  const applyDiscountCode = async (code) => {
    const normalized = normalizeCode(code);
    if (!normalized) return false;

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}cart/update.js`;

    const tryJson = () =>
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: normalized }),
      });

    const tryForm = () => {
      const body = new FormData();
      body.append('discount', normalized);
      return fetch(url, { method: 'POST', body });
    };

    let response = await tryJson();
    if (!response.ok) {
      response = await tryForm();
    }
    if (!response.ok) return false;

    const stored = readStoredCodes();
    if (!stored.includes(normalized)) {
      stored.push(normalized);
      writeStoredCodes(stored);
    }

    return true;
  };

  const syncDiscountCodes = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`);
    if (!response.ok) return;

    const cart = await response.json();
    const csv = buildCodesCsv(cart);
    const previous = document.documentElement.dataset.voDkAppliedCodes || '';

    document.documentElement.dataset.voDkAppliedCodes = csv;

    if (csv !== previous) {
      document.dispatchEvent(
        new CustomEvent('vo:dk:discount-codes-changed', { detail: { appliedCodes: csv } })
      );
    }

    window.VoDkStorefrontPrices?.syncAppliedCodesFromCookies?.();
    window.VoDkStorefrontPrices?.refresh?.();
  };

  const bootstrap = async () => {
    const urlCode = resolveDiscountCodeFromUrl();
    const seedCodes = mergeCodes(
      urlCode ? [urlCode] : [],
      SHOPIFY_CODE_COOKIES.map(getCookie).filter(Boolean),
      readStoredCodes()
    );

    if (seedCodes.length) {
      writeStoredCodes(seedCodes);
      window.VoDkStorefrontPrices?.syncAppliedCodesFromCookies?.();
      window.VoDkStorefrontPrices?.refresh?.();
    }

    if (urlCode) {
      await applyDiscountCode(urlCode);
    }

    await syncDiscountCodes();
  };

  bootstrap();

  document.addEventListener('cart-update', syncDiscountCodes);
})();
